import type { HTMLAttributes } from 'react';
import classNames from 'classnames';
import { Image } from '$components/basic/Image';
import { Link } from '$components/basic/Link';
import { useAppContext } from '$util/AppContext';
import { trimSpacesFromString } from '$util/stringOperations/trimSpacesFromString';
import type { CmsContentDictionary, CmsContentProps } from '$cms/index';
import { getImageByKey, getShortTextByKey } from '$cms/getDataByKey';

import styles from './index.module.scss';

const getCmsData = (cmsData: CmsContentDictionary) => {
  const getShortText = getShortTextByKey.bind(cmsData);
  const getImage = getImageByKey.bind(cmsData);

  return {
    callUsAreaCode: getShortText('vrmLookup.callUs.areaCode', '44'),
    callUsText: getShortText('vrmLookup.callUs.text', 'or call us on'),
    callUsPhoneNumber: getShortText('vrmLookup.callUs.phoneNumber', '0330 808 9527'),
    callUsImage: getImage('vrmLookup.callUs.image', {
      img: { original_secure_url: '/icons/icon_phone_black_hrde7r.svg', width: 14, height: 18 },
      alt: 'Phone',
      title: '',
    }).img.original_secure_url,
    callUsImageAlt: getImage('vrmLookup.callUs.image', {
      img: { original_secure_url: '/icons/icon_phone_black_hrde7r.svg', width: 14, height: 18 },
      alt: 'Phone',
      title: '',
    }).alt,
    callUsImageTitle: getImage('vrmLookup.callUs.image', {
      img: { original_secure_url: '/icons/icon_phone_black_hrde7r.svg', width: 14, height: 18 },
      alt: 'Phone',
      title: '',
    }).title,
  };
};

export function CallUs({ className }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  const {
    cmsContent: { dictionary },
  } = useAppContext<CmsContentProps>();

  const { callUsAreaCode, callUsText, callUsPhoneNumber, callUsImage, callUsImageAlt, callUsImageTitle } =
    getCmsData(dictionary);

  return (
    <div className={classNames(styles.component, className)}>
      <Image
        className={styles.icon}
        provider="cloudinary"
        layout="fixed"
        src={callUsImage}
        alt={callUsImageAlt}
        title={callUsImageTitle}
        width={14}
        height={18}
      />
      <span>
        {callUsText}{' '}
        <Link href={`tel:+${callUsAreaCode}${trimSpacesFromString(callUsPhoneNumber)}`}>
          {callUsPhoneNumber}
        </Link>
      </span>
    </div>
  );
}

CallUs.displayName = 'CallUs';
